import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { getPostBySlug, getRelatedPosts } from '../data/posts';
import SEO from '../components/SEO';
import styles from './BlogPostPage.module.css';

/* ── Helpers ──────────────────────────────────────────────── */
function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function buildSchema(post) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type':         'BlogPosting',
        '@id':           `https://movementgreen.com.au/blog/${post.slug}#article`,
        headline:        post.title,
        description:     post.description,
        datePublished:   post.date,
        dateModified:    post.date,
        url:             `https://movementgreen.com.au/blog/${post.slug}`,
        image:           'https://movementgreen.com.au/og-image.jpg',
        inLanguage:      'en-AU',
        author: {
          '@type': 'Organization',
          '@id':   'https://movementgreen.com.au/#business',
          name:    'Movement Green',
        },
        publisher: {
          '@type': 'Organization',
          '@id':   'https://movementgreen.com.au/#business',
          name:    'Movement Green',
          logo: {
            '@type': 'ImageObject',
            url:    'https://movementgreen.com.au/og-image.jpg',
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id':   `https://movementgreen.com.au/blog/${post.slug}`,
        },
        keywords: post.keywords,
        articleSection: post.category,
        about: {
          '@type': 'Thing',
          name:    'Victorian Energy Upgrades',
          sameAs:  'https://www.esc.vic.gov.au/victorian-energy-upgrades',
        },
      },
      ...(post.faq?.length ? [{
        '@type':      'FAQPage',
        '@id':        `https://movementgreen.com.au/blog/${post.slug}#faq`,
        mainEntity:   post.faq.map(({ q, a }) => ({
          '@type':          'Question',
          name:             q,
          acceptedAnswer:   { '@type': 'Answer', text: a },
        })),
      }] : []),
    ],
  };
}

/* ── Section renderer ─────────────────────────────────────── */
function Section({ sec }) {
  return (
    <div className={styles.section}>
      <h2 className={styles.h2}>{sec.heading}</h2>
      {sec.paragraphs?.map((p, i) => (
        <p key={i} className={styles.para}>{p}</p>
      ))}
      {sec.list && (
        sec.list.ordered
          ? <ol className={styles.list}>
              {sec.list.items.map((item, i) => <li key={i}>{item}</li>)}
            </ol>
          : <ul className={styles.list}>
              {sec.list.items.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
      )}
      {sec.tip && (
        <div className={styles.tip}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M21 3C21 3 12 3 7 8C2 13 3 21 3 21C3 21 11 22 16 17C21 12 21 3 21 3Z" fill="#B5D96A"/>
          </svg>
          <p>{sec.tip}</p>
        </div>
      )}
      {sec.note && (
        <div className={styles.note}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#6B7A6C" strokeWidth="1.5"/>
            <path d="M12 8v4M12 16h.01" stroke="#6B7A6C" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <p>{sec.note}</p>
        </div>
      )}
    </div>
  );
}

/* ── FAQ Accordion ────────────────────────────────────────── */
function FaqAccordion({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <div className={styles.faqList}>
      {items.map((item, i) => (
        <div key={i} className={`${styles.faqItem} ${open === i ? styles.faqItemOpen : ''}`}>
          <button
            className={styles.faqQ}
            onClick={() => setOpen(open === i ? null : i)}
            aria-expanded={open === i}
          >
            <span>{item.q}</span>
            <svg className={styles.faqIcon} width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className={styles.faqA} aria-hidden={open !== i}>
            <p>{item.a}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────── */
export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const related = getRelatedPosts(slug, 3);

  return (
    <main className="page-enter">
      <SEO
        title={`${post.title} | Movement Green`}
        description={post.description}
        keywords={post.keywords}
        schema={buildSchema(post)}
      />

      {/* ── HERO ──────────────────────────────────────── */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroBreadcrumb}>
            <Link to="/blog" className={styles.breadLink}>Blog</Link>
            <span className={styles.breadSep}>/</span>
            <span className={styles.breadCurrent}>{post.category}</span>
          </div>
          <div className={styles.heroMeta}>
            <span className={styles.catBadge}>{post.category}</span>
            <span className={styles.metaDate}>{formatDate(post.date)}</span>
            <span className={styles.metaDot}>·</span>
            <span className={styles.metaRead}>{post.readTime}</span>
          </div>
          <h1 className={`disp-lg ${styles.heroTitle}`}>{post.title}</h1>
          <p className={styles.heroIntro}>{post.intro}</p>
        </div>
      </section>

      {/* ── ARTICLE BODY ──────────────────────────────── */}
      <section className={styles.articleSection}>
        <div className={styles.articleInner}>

          {/* Main content */}
          <article className={styles.article}>
            {post.sections.map((sec, i) => (
              <Section key={i} sec={sec} />
            ))}

            {/* FAQ */}
            {post.faq?.length > 0 && (
              <div className={styles.faqBlock}>
                <h2 className={styles.faqTitle}>Frequently Asked Questions</h2>
                <FaqAccordion items={post.faq} />
              </div>
            )}

            {/* Article CTA */}
            <div className={styles.articleCta}>
              <div>
                <div className={styles.articleCtaTitle}>Ready to access your VEU<sup>*</sup> benefit?</div>
                <p className={styles.articleCtaDesc}>
                  Book a free home energy assessment with Movement Green.
                  We identify every upgrade your Victorian home qualifies for — no obligation.
                </p>
              </div>
              <Link to="/contact" className="btn btn-primary btn-lg">Book Free Assessment →</Link>
            </div>
          </article>

          {/* Sidebar */}
          <aside className={styles.sidebar}>
            <div className={styles.sideCard}>
              <div className={styles.sideTitle}>Free Energy Assessment</div>
              <p className={styles.sideDesc}>
                Find out exactly which VEU<sup>*</sup> benefits your Victorian home qualifies for.
                No cost, no obligation.
              </p>
              <Link to="/contact" className={`btn btn-primary ${styles.sideBtn}`}>
                Book Assessment →
              </Link>
            </div>

            <div className={styles.sideCard}>
              <div className={styles.sideTitle}>Available Upgrades</div>
              <div className={styles.sideLinks}>
                {[
                  ['Heat Pump Hot Water', '/solutions'],
                  ['Reverse-Cycle AC',   '/solutions'],
                  ['LED Lighting',       '/solutions'],
                  ['Commercial Solar',    '/solutions'],
                ].map(([label, path]) => (
                  <Link key={label} to={path} className={styles.sideLink}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M21 3C21 3 12 3 7 8C2 13 3 21 3 21C3 21 11 22 16 17C21 12 21 3 21 3Z" fill="#B5D96A"/>
                    </svg>
                    {label}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* ── RELATED POSTS ─────────────────────────────── */}
      {related.length > 0 && (
        <section className={styles.relatedSection}>
          <div className={styles.relatedInner}>
            <p className={styles.relatedLabel}>More Articles</p>
            <div className={styles.relatedGrid}>
              {related.map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className={styles.relatedCard}>
                  <span className={styles.catBadge}>{p.category}</span>
                  <h3 className={`h-md ${styles.relatedTitle}`}>{p.title}</h3>
                  <div className={styles.relatedMeta}>
                    <span className={styles.metaDate}>{formatDate(p.date)}</span>
                    <span className={styles.metaDot}>·</span>
                    <span className={styles.metaRead}>{p.readTime}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
